export enum OrganizationRoleEnum {
  ULTIMATE_USER = "ULTIMATE_USER",
  MEGA_USER = "MEGA_USER",
  REGION_ADMINISTRATOR = "REGION_ADMINISTRATOR",
  AGENCY_ADMINISTRATOR = "AGENCY_ADMINISTRATOR",
  SUPER_USER = "SUPER_USER",
  NORMAL_USER = "NORMAL_USER",
  DISABLED = "DISABLED",
}

export enum OrganizationRoleEnumForSelect {
  TOUS = "TOUS",
  MEGA_USER = "MEGA_USER",
  REGION_ADMINISTRATOR = "REGION_ADMINISTRATOR",
  AGENCY_ADMINISTRATOR = "AGENCY_ADMINISTRATOR",
  SUPER_USER = "SUPER_USER",
  NORMAL_USER = "NORMAL_USER",
}

export const organizationRoleEnumEnumLabels = {
  [OrganizationRoleEnumForSelect.MEGA_USER]: roleOnOrganizationToLabel(OrganizationRoleEnum.MEGA_USER),
  [OrganizationRoleEnumForSelect.REGION_ADMINISTRATOR]: roleOnOrganizationToLabel(OrganizationRoleEnum.REGION_ADMINISTRATOR),
  [OrganizationRoleEnumForSelect.AGENCY_ADMINISTRATOR]: roleOnOrganizationToLabel(OrganizationRoleEnum.AGENCY_ADMINISTRATOR),
  [OrganizationRoleEnumForSelect.SUPER_USER]: roleOnOrganizationToLabel(OrganizationRoleEnum.SUPER_USER),
  [OrganizationRoleEnumForSelect.NORMAL_USER]: roleOnOrganizationToLabel(OrganizationRoleEnum.NORMAL_USER),
  [OrganizationRoleEnumForSelect.TOUS]: "Tous",
}

/** US 2530: the project manager and every role above them, the agency, region and organization administrators. */
export function isAtLeastSuperUser(role: OrganizationRoleEnum | undefined): boolean {
  return (
    role === OrganizationRoleEnum.MEGA_USER ||
    role === OrganizationRoleEnum.REGION_ADMINISTRATOR ||
    role === OrganizationRoleEnum.AGENCY_ADMINISTRATOR ||
    role === OrganizationRoleEnum.SUPER_USER
  )
}

/**
 * US 2530: the organization, region and agency administrators, who see and manage projects beyond their own team (the
 * whole organization, or the projects of the agencies they administer).
 */
export function isAdministratorRole(role: OrganizationRoleEnum | undefined): boolean {
  return (
    role === OrganizationRoleEnum.MEGA_USER ||
    role === OrganizationRoleEnum.REGION_ADMINISTRATOR ||
    role === OrganizationRoleEnum.AGENCY_ADMINISTRATOR
  )
}

/**
 * Mirrors OrganizationRoleEnum.organizationRank on the backend: the hierarchy inside an organization, lowest first.
 * The Time To Beem staff sits above every organization and is ranked in none, hence 0.
 */
const ORGANIZATION_RANK: Partial<Record<OrganizationRoleEnum, number>> = {
  [OrganizationRoleEnum.MEGA_USER]: 5,
  [OrganizationRoleEnum.REGION_ADMINISTRATOR]: 4,
  [OrganizationRoleEnum.AGENCY_ADMINISTRATOR]: 3,
  [OrganizationRoleEnum.SUPER_USER]: 2,
  [OrganizationRoleEnum.NORMAL_USER]: 1,
}

export function organizationRank(role: OrganizationRoleEnum | undefined): number {
  return (role && ORGANIZATION_RANK[role]) ?? 0
}

/**
 * US 2596: the roles an administrator hands out — every role of the organization up to their own rank, never above.
 * An agency administrator names members, project managers and agency administrators; a region administrator names
 * region administrators too; only the organization administrator names its own kind. The server applies the same
 * rule, in OrganizationRoleEnum.canGrant.
 */
export function canGrantRole(
  administrator: OrganizationRoleEnum | undefined,
  granted: OrganizationRoleEnum | undefined
): boolean {
  const grantedRank = organizationRank(granted)
  return isAdministratorRole(administrator) && grantedRank > 0 && grantedRank <= organizationRank(administrator)
}

export function roleOnOrganizationToLabel(role: OrganizationRoleEnum): string {
  if (role) {
    switch (role) {
      case OrganizationRoleEnum.MEGA_USER:
        return "Administrateur de l'organisation"
      case OrganizationRoleEnum.REGION_ADMINISTRATOR:
        return "Administrateur de région"
      case OrganizationRoleEnum.AGENCY_ADMINISTRATOR:
        return "Administrateur d'agence"
      case OrganizationRoleEnum.SUPER_USER:
        return "Chef de projets"
      case OrganizationRoleEnum.NORMAL_USER:
        return "Membre"
      default:
        return ""
    }
  } else {
    return ""
  }
}

/**
 * Tooltip of the project-role indicator: "Vous êtes <rôle>" for a team member,
 * the "externe" wording when the user is not in the project team (null role),
 * and null (indicator hidden) for roles without a displayable label.
 */
export function roleOnProjectTooltip(role: OrganizationRoleEnum | null | undefined): string | null {
  if (!role) {
    return "Vous êtes externe à l'équipe projet"
  }
  const label = roleOnOrganizationToLabel(role)
  return label ? `Vous êtes ${label}` : null
}
